'use client';

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { X } from 'lucide-react';
import AddConnectorApiStep from '@/components/add-connector-modal-steps/add-connector-api-step';
import AddConnectorAuthenticationStep from '@/components/add-connector-modal-steps/add-connector-authentication-step';
import AddConnectorProfileStep from '@/components/add-connector-modal-steps/add-connector-profile-step';
import AddConnectorReviewStep from '@/components/add-connector-modal-steps/add-connector-review-step';
import ConnectorCreatedModal, { type CreatedConnector } from '@/components/connector-created-modal';
import { Button } from '@/components/ui';
import UnsavedChangesModal from '@/components/unsaved-changes-modal';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  clearFlowState,
  getFlowCreationSystemId,
  getFlowState,
  hasUnsavedChanges,
  markStepVisited,
  updateFlowState,
} from '@/lib/connector-flow-state';
import { useConnectorFlowSnapshot } from '@/hooks/use-connector-flow-snapshot';
import { domId } from '@/lib/dom-id';
import type { AuthConfig } from '@/services/auth-config-service';
import { revalidateOnboardingProgress } from '@/services/onboarding-service';
import { deleteSystem } from '@/services/system-service';

type AddConnectorModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialStep?: ModalStep;
  dismissMode?: 'guard' | 'preserve';
};

type ModalStep = 'profile' | 'add-api' | 'review' | 'authentication';
type StepItem = { number: number; label: string };

const BRAND_GRADIENT = 'linear-gradient(90deg, #713D98 0%, #3E3D98 140.38%)';
const CONNECTOR_FLOW_WIDTH = 850;

function resolveModalStepFromFlowState(
  initialStep: ModalStep,
  flowState: ReturnType<typeof getFlowState>
): ModalStep {
  if (initialStep !== 'profile') {
    return initialStep;
  }

  if (flowState.currentStep >= 3 && flowState.systemId) {
    return 'authentication';
  }

  if (flowState.currentStep >= 2) {
    return flowState.previewActions.length > 0 ? 'review' : 'add-api';
  }

  return 'profile';
}

function ConnectorModalStepper({
  currentStep,
  steps,
  visitedSteps,
  hasConnectorName,
  onStepClick,
}: {
  currentStep: number;
  steps: StepItem[];
  visitedSteps: number[];
  hasConnectorName: boolean;
  onStepClick: (stepNumber: number) => void;
}) {
  // Steps never shrink below their number bubble and the connectors shrink
  // from 80px, so the stepper fits narrow windows instead of being clipped.
  const stepperGridTemplateColumns = steps
    .flatMap((_, index) =>
      index === steps.length - 1 ? ['minmax(3rem,1fr)'] : ['minmax(3rem,1fr)', 'minmax(1rem,80px)']
    )
    .join(' ');

  return (
    <div className="w-full overflow-hidden">
      <div
        className="grid items-center"
        style={{
          gridTemplateColumns: stepperGridTemplateColumns,
        }}
      >
        {steps.map((step, index) => {
          const isActive = step.number === currentStep;
          const isLast = index === steps.length - 1;
          const isClickable =
            visitedSteps.includes(step.number) && (step.number === 1 || hasConnectorName);
          const isVisited = visitedSteps.includes(step.number);

          return (
            <Fragment key={step.number}>
              <div
                className={`flex min-w-0 ${
                  index === 0 ? 'justify-start' : isLast ? 'justify-end' : 'justify-center'
                }`}
              >
                <Button
                  data-testid={domId('btn', `add-connector-modal-stepper-step-${step.label}`)}
                  type="button"
                  variant="unstyled"
                  className={`group/step inline-flex min-w-0 items-center gap-2 rounded-full focus-visible:outline-none ${
                    isClickable ? 'cursor-pointer' : 'cursor-not-allowed'
                  }`}
                  onClick={() => onStepClick(step.number)}
                  disabled={!isClickable}
                  aria-label={step.label}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full border text-[20px] font-semibold leading-[1.4] transition-[border-color,background-color,color] duration-200 ease-out ${
                      isActive
                        ? 'border-white bg-[#9333EA] text-white'
                        : isVisited
                          ? 'border-transparent bg-transparent text-white'
                          : 'border-[#8E9BA8] bg-white text-[#8E9BA8]'
                    }`}
                    style={isVisited && !isActive ? { backgroundImage: BRAND_GRADIENT } : undefined}
                  >
                    {step.number}
                  </div>
                  <span
                    className={`hidden min-w-0 truncate text-[16px] font-semibold leading-[1.4] transition-[color,opacity] duration-200 ease-out sm:block ${
                      isActive
                        ? 'text-[#8E9BA8]'
                        : isVisited
                          ? 'bg-clip-text text-transparent'
                          : 'text-[#8E9BA8]'
                    } ${
                      isClickable && !isVisited
                        ? 'group-hover/step:text-[#6D7E91] group-focus-visible/step:text-[#6D7E91]'
                        : ''
                    } ${
                      isClickable && isActive
                        ? 'group-hover/step:text-[#6D7E91] group-focus-visible/step:text-[#6D7E91]'
                        : ''
                    }`}
                    style={isVisited && !isActive ? { backgroundImage: BRAND_GRADIENT } : undefined}
                  >
                    {step.label}
                  </span>
                </Button>
              </div>

              {!isLast ? (
                <div
                  aria-hidden="true"
                  className="h-[0.5px] w-full"
                  style={{ backgroundImage: BRAND_GRADIENT }}
                />
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default function AddConnectorModal({
  open,
  onOpenChange,
  initialStep = 'profile',
  dismissMode = 'guard',
}: AddConnectorModalProps) {
  const t = useTranslations('ConnectorNew');
  const tLayout = useTranslations('ConnectorsLayout');
  const [step, setStep] = useState<ModalStep>('profile');
  const [systemId, setSystemId] = useState('');
  const [systemName, setSystemName] = useState('');
  const [authDraft, setAuthDraft] = useState<Partial<AuthConfig> | null>(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [visitedSteps, setVisitedSteps] = useState<number[]>([1]);
  const [createdConnector, setCreatedConnector] = useState<CreatedConnector | null>(null);
  const flowSnapshot = useConnectorFlowSnapshot();
  const hasConnectorName = Boolean(flowSnapshot.connectorData?.name?.trim());
  // Steps read the browser draft while they render, which happens before this
  // component's open effect clears (guard) or restores (preserve) it. Mount
  // them only after that effect ran so a fresh draft never shows stale data.
  const [draftReady, setDraftReady] = useState(false);

  const currentStepNumber = useMemo(() => {
    if (step === 'authentication') return 3;
    if (step === 'add-api' || step === 'review') return 2;
    return 1;
  }, [step]);

  const steps = useMemo<StepItem[]>(
    () => [
      { number: 1, label: tLayout('steps.profile') },
      { number: 2, label: tLayout('steps.addApi') },
      { number: 3, label: tLayout('steps.authentication') },
    ],
    [tLayout]
  );

  const resetLocalState = useCallback(() => {
    setStep(initialStep);
    setSystemId('');
    setSystemName('');
    setAuthDraft(null);
    setShowDiscardModal(false);
    setVisitedSteps([1]);
    setDraftReady(false);
  }, [initialStep]);

  useEffect(() => {
    if (!open) {
      resetLocalState();
    }
  }, [open, resetLocalState]);

  useEffect(() => {
    if (!open) return;

    // Dashboard Add Connector starts a fresh draft. Onboarding entries use
    // "preserve" so their resumable flow state remains intact.
    if (dismissMode === 'guard' && initialStep === 'profile') {
      clearFlowState();
    }

    const flowState = getFlowState();
    const resolvedStep = resolveModalStepFromFlowState(initialStep, flowState);
    const resolvedSystemId = flowState.systemId ?? '';
    const resolvedSystemName = flowState.connectorData?.name?.trim() || flowState.systemName || '';

    setStep(resolvedStep);
    setSystemId(resolvedSystemId);
    setSystemName(resolvedSystemName);

    const nextVisitedSteps =
      flowState.visitedSteps && flowState.visitedSteps.length > 0
        ? flowState.visitedSteps
        : resolvedStep === 'authentication'
          ? [1, 2, 3]
          : resolvedStep === 'add-api' || resolvedStep === 'review'
            ? [1, 2]
            : [1];
    setVisitedSteps(nextVisitedSteps);
    setDraftReady(true);
  }, [dismissMode, initialStep, open]);

  useEffect(() => {
    if (!open) return;
    const state = markStepVisited(currentStepNumber);
    setVisitedSteps(state.visitedSteps);
  }, [currentStepNumber, open]);

  const closeImmediately = useCallback(() => {
    resetLocalState();
    onOpenChange(false);
  }, [onOpenChange, resetLocalState]);

  const handleRequestClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowDiscardModal(true);
      return;
    }

    clearFlowState();
    closeImmediately();
  }, [closeImmediately, dismissMode]);

  const handleDiscardChanges = useCallback(async () => {
    setShowDiscardModal(false);

    const flowState = getFlowState();
    // Delete only the connector persisted by this creation flow.
    const systemToDelete = getFlowCreationSystemId(flowState);
    // Discard is a local UI action first. Clear the browser draft and close the
    // modal immediately so a slow/failed cleanup request cannot resurrect the
    // previous step or preview actions when the flow is reopened.
    clearFlowState();
    closeImmediately();

    if (systemToDelete) {
      try {
        await deleteSystem(systemToDelete);
        // Home guidance cached its progress while the connector still existed;
        // refetch so the Getting Started steps drop the deleted connector.
        await revalidateOnboardingProgress();
      } catch (err) {
        console.error('Failed to delete system:', err);
      }
    }
  }, [closeImmediately, dismissMode]);

  const dialogWidthClass = step === 'add-api'
    ? 'flex flex-col w-[calc(100vw-32px)] max-w-[1280px]'
    : 'w-[calc(100vw-32px)] max-w-[1180px]';
  const dialogLayoutClass =
    step === 'review'
      ? 'flex h-[90vh] max-h-[90vh] flex-col overflow-hidden'
      : 'max-h-[90vh] overflow-y-auto';

  const handleStepClick = useCallback(
    (stepNumber: number) => {
      if (!visitedSteps.includes(stepNumber)) return;
      if (stepNumber > 1 && !hasConnectorName) return;

      if (stepNumber === 1) {
        setStep('profile');
        return;
      }

      if (stepNumber === 2) {
        const flowState = getFlowState();
        const hasReviewData = flowState.previewActions.length > 0 || step === 'review';
        setStep(hasReviewData ? 'review' : 'add-api');
        return;
      }

      if (stepNumber === 3 && systemId) {
        setStep('authentication');
      }
    },
    [step, systemId, visitedSteps, hasConnectorName]
  );

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={nextOpen => {
          if (nextOpen) {
            onOpenChange(true);
            return;
          }

          handleRequestClose();
        }}
      >
        <DialogContent
          showClose={false}
          className={`${dialogWidthClass} ${dialogLayoutClass} rounded-2xl p-8 ${
            showDiscardModal ? 'pointer-events-none' : ''
          }`}
          onPointerDownOutside={event => {
            event.preventDefault();
            handleRequestClose();
          }}
          onEscapeKeyDown={event => {
            event.preventDefault();
            handleRequestClose();
          }}
        >
          <DialogTitle className="sr-only">{t('title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('subtitle')}</DialogDescription>

          <button
            type="button"
            onClick={handleRequestClose}
            className="absolute right-6 top-6 flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label={t('actions.cancel')}
          >
            <X className="h-4 w-4" />
          </button>

          <div
            className="mx-auto flex w-full flex-col gap-8"
            style={{ maxWidth: step === 'add-api' ? '1060px' : `${CONNECTOR_FLOW_WIDTH}px` }}
          >
            <h1 className="text-[28px] font-semibold leading-[1.4] text-[#0B0809]">
              {tLayout('title')}
            </h1>

            <ConnectorModalStepper
              currentStep={currentStepNumber}
              steps={steps}
              visitedSteps={visitedSteps}
              hasConnectorName={hasConnectorName}
              onStepClick={handleStepClick}
            />
          </div>

          {draftReady && step === 'profile' && (
            <AddConnectorProfileStep
              open={open}
              onRequestClose={handleRequestClose}
              onContinue={payload => {
                setSystemId(payload.systemId);
                setSystemName(payload.name);
                setStep('add-api');
              }}
            />
          )}

          {draftReady && step === 'add-api' && (
            <AddConnectorApiStep
              systemId={systemId}
              systemName={systemName}
              onRequestClose={handleRequestClose}
              onContinue={payload => {
                setSystemId(payload.systemId);
                setSystemName(payload.systemName);
                setStep('review');
              }}
            />
          )}

          {draftReady && step === 'review' && (
            <AddConnectorReviewStep
              systemId={systemId}
              onRequestClose={handleRequestClose}
              onBack={() => {
                // Importing another spec replaces this preview. Drop it so the
                // upload step shows its form instead of forwarding back here;
                // the connector persisted in step 2 is reused.
                updateFlowState({ previewActions: [], hasProcessedApi: false });
                setStep('add-api');
              }}
              onContinue={() => setStep('authentication')}
            />
          )}

          {draftReady && step === 'authentication' && (
            <AddConnectorAuthenticationStep
              systemId={systemId}
              draftConfig={authDraft}
              onDraftChange={setAuthDraft}
              onRequestClose={handleRequestClose}
              onCompleted={connector => {
                setCreatedConnector(connector);
                closeImmediately();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <UnsavedChangesModal
        isOpen={showDiscardModal}
        onKeepEditing={() => setShowDiscardModal(false)}
        onDiscard={() => {
          void handleDiscardChanges();
        }}
      />

      <ConnectorCreatedModal
        connector={createdConnector}
        onClose={() => setCreatedConnector(null)}
      />
    </>
  );
}
